
import React from 'react';
import Logo from './Logo';
import { AppSection } from '../types';

// Define props to match App.tsx usage
interface AboutProps {
  onNavigate: (section: AppSection) => void;
  onContactClick: () => void;
}

const About: React.FC<AboutProps> = ({ onNavigate, onContactClick }) => {
  const values = [
    { title: "Precision", desc: "No room for technical ambiguity. We document, validate, and verify every architectural decision." },
    { title: "Velocity", desc: "Midwest work ethic meets modern tech agility. We prioritize shipping value over bloated process." },
    { title: "Transparency", desc: "Direct access to senior leadership and clear, honest reporting on project health." }
  ];

  return (
    <div className="bg-white min-h-screen">
      <section className="py-32 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="mono text-xs text-[#0091da] font-bold mb-8 tracking-[0.4em] uppercase">// The Human Grid</div>
            <h1 className="text-6xl md:text-8xl font-bold text-slate-950 mb-12 tracking-tighter leading-[0.9]">Built for the <br/><span className="text-[#facc15]">Chicago Economy.</span></h1>
            <div className="flex flex-col md:flex-row gap-16 items-start">
              <p className="text-xl text-slate-600 font-light leading-relaxed md:w-2/3">
                Hunargrid was founded to bridge the widening gap between high-level management consulting and hands-on technical execution. Based in the heart of Chicago, we combine global talent with Midwest reliability.
              </p>
              <div className="md:w-1/3 flex items-center space-x-6">
                <div className="h-24 w-px bg-slate-200"></div>
                <div className="text-sm text-slate-400 italic font-light">"Our mission is to provide the architectural clarity required to scale at speed."</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-16">
            {values.map((v, i) => (
              <div key={i}>
                <div className="w-12 h-1 bg-[#0091da] mb-8"></div>
                <h3 className="text-xl font-bold mb-4">{v.title}</h3>
                <p className="text-slate-500 text-sm font-light leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-32 bg-white overflow-hidden relative">
        <div className="absolute inset-0 grid-bg opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-24">
            <div className="lg:w-1/2">
               <h2 className="text-4xl font-bold mb-8 tracking-tight">Enterprise Stability. <br/> Startup Velocity.</h2>
               <p className="text-lg text-slate-600 mb-10 font-light leading-relaxed">
                 Whether you are a Series B startup looking to professionalize your engineering or a Fortune 500 entity modernizing a legacy system, Hunargrid provides the 'Strategic Engine' to make it happen.
               </p>
               <div className="flex items-center space-x-4">
                  <Logo className="w-12 h-12" />
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Established 2025</div>
                    <div className="text-sm font-bold text-slate-900">Privately Owned // Chicago, IL</div>
                  </div>
               </div>
            </div>
            <div className="lg:w-1/2 w-full grid grid-cols-2 gap-4">
               <div className="aspect-[4/5] bg-slate-100 rounded-sm overflow-hidden relative flex items-center justify-center group border border-slate-200">
                  <div className="text-center p-6 transition-all group-hover:scale-105">
                    <div className="w-16 h-16 bg-slate-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <svg className="w-8 h-8 text-slate-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg>
                    </div>
                    <div className="mono text-[10px] text-slate-400 uppercase tracking-widest mb-1">Founding Partner</div>
                    <div className="text-xs font-bold text-slate-800 uppercase">Architecture & Strategy</div>
                  </div>
               </div>
               <div className="aspect-[4/5] bg-slate-50 rounded-sm overflow-hidden relative mt-8 flex items-center justify-center group border border-slate-200">
                  <div className="text-center p-6 transition-all group-hover:scale-105">
                    <div className="w-16 h-16 bg-slate-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <svg className="w-8 h-8 text-slate-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg>
                    </div>
                    <div className="mono text-[10px] text-slate-400 uppercase tracking-widest mb-1">Managing Director</div>
                    <div className="text-xs font-bold text-slate-800 uppercase">Engineering Operations</div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
